/**
 * The Monday garden email, on the profile: weekly (every Monday morning),
 * monthly (the first Monday of the month) or off. Kept the moment it
 * changes, with "Saved" beside it, as every choice on the profile is.
 *
 * It is its own row, beside Weather warnings rather than inside it, because
 * the two are separate: switching this off never touches warning emails.
 * The email's own links ("Send me this monthly instead", "Unsubscribe")
 * change the same choice (MondayEmailLinkPage).
 *
 * A read or a save that fails on our side (a 5xx or no answer) says the
 * standard sentence, reported to Sentry, with the support address as a link
 * and no "Try again" (D31); other failures keep their own words.
 *
 * Used by: UserProfile (?s=monday).
 *
 * @module MondayEmailScreen
 */

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getMondayEmail, setMondayEmail, MONDAY_FREQUENCIES, type MondayFrequency } from '@/shared/api/gardenEmail';
import { ProfileScreen } from './ProfileScreen';
import { Segmented, SettingList, SettingRow } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { MONDAY_EMAIL_KEY } from './profileQueries';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure, rateLimitedText } from '@/shared/lib/serverFailure';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';

/** The Monday garden email screen: weekly, monthly or off. */
export const MondayEmailScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  // A read that failed is said, rather than "Loading" for ever.
  const { data, isLoading, isError, error: loadError } = useQuery({ queryKey: MONDAY_EMAIL_KEY, queryFn: getMondayEmail, enabled: open });
  const { saved, flash } = useSavedTicks();
  const [error, setError] = React.useState<string | null>(null);
  /** Keeps the choice, ticking "Saved" beside it. */
  const save = useMutation({
    mutationFn: setMondayEmail,
    onMutate: () => setError(null),
    onSuccess: (next) => { queryClient.setQueryData(MONDAY_EMAIL_KEY, next); flash('monday'); },
    onError: (err) => setError(errorMessage(err, t('profile.monday.saveFailed'))),
  });

  return (
    <ProfileScreen open={open} title={t('profile.monday.title')} onClose={onClose}>
      {!data && isError ? (
        isServerFailure(loadError)
          ? <ServerFailureNote error={loadError} where="MondayEmailScreen" />
          : <p role="alert" className="text-sm text-destructive"><FailureText text={rateLimitedText(loadError) ?? t('sitewide.loadFailed')} /></p>
      ) : isLoading || !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      ) : (
        <>
          <p className="text-sm">{t('profile.monday.about')}</p>
          <SettingList>
            <SettingRow
              title={t('profile.monday.howOften')}
              hint={t('profile.monday.hint')}
              saved={saved('monday')}
              stacked
            >
              <Segmented<MondayFrequency>
                value={save.isPending && save.variables ? save.variables : data.frequency}
                options={MONDAY_FREQUENCIES.map((value) => ({ value, label: t(`profile.monday.${value}`) }))}
                onChange={(next) => save.mutate(next)}
                label={t('profile.monday.howOften')}
                disabled={save.isPending}
              />
            </SettingRow>
          </SettingList>
          {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
        </>
      )}
    </ProfileScreen>
  );
};
